import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import classNames from "classnames";
import { Camera, Check, Gift, Image as ImageIcon } from "lucide-react";
import { backendLinkLottery } from "../config";
import { styles, ErrorBox, Field, PageShell, Select, ThanksScreen, cleanText, flagField, readImages, toDigits, useLocations } from "../components/wizard";

const ReceiptCamera = lazy(() => import("../components/ReceiptCamera"));
const ParticipationTermsModal = lazy(() => import("../components/ParticipationTermsModal"));

// Tombola (JawdaPlus_Tombola_B.html): your details, the photo of the till receipt, then the
// confirmation. Sends what production's contest form sends (FormulaireConcours, addLotteryForm):
// name, phone, willaya (French name), img_l, as the tenant's lotteryFormFields ask.

// production keeps the details of the last participation on the phone
const SAVED_KEY = "formData";
const readSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(SAVED_KEY)) || {};
  } catch (_) {
    return {};
  }
};

const phoneOk = (phone) => /^0[567]\d{8}$/.test(phone); // mobile: 10 digits, 05 / 06 / 07
const spacedTel = (phone) => phone.replace(/(\d{4})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4");

const DoneRow = ({ title, sub, thumb, onEdit }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.tbDone}>
      <span className={styles.tbCk}>
        <Check size={16} strokeWidth={3} aria-hidden />
      </span>
      {thumb && <img className={styles.tbThumb} src={thumb} alt="" />}
      <div className={styles.tbTx}>
        <strong>{title}</strong>
        {sub && <small>{sub}</small>}
      </div>
      <button type="button" className={styles.tbEdit} onClick={onEdit}>
        {t("tbEdit")}
      </button>
    </div>
  );
};

const TodoRow = ({ n, title }) => (
  <div className={styles.tbTodo}>
    <span className={styles.tbNum}>{n}</span>
    <strong>{title}</strong>
  </div>
);

const StepCard = ({ n, title, children }) => (
  <section className={styles.tbCard}>
    <div className={styles.tbCardH}>
      <span className={styles.tbNum}>{n}</span>
      <strong>{title}</strong>
    </div>
    {children}
  </section>
);

export default function TombolaForm({ currentLang, setCurrentLang }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const enabledModules = useSelector((state) => state.tenant?.tenant.enabledModules) || {};
  const fields = useSelector((state) => state.tenant.tenant.lotteryFormFields) || [];
  const obligatory = useSelector((state) => state.tenant.tenant.obligatoryLotteryFormFields) || [];
  const { wilayas } = useLocations();

  const saved = useRef(readSaved()).current;
  const [name, setName] = useState(saved.name || "");
  const [phone, setPhone] = useState(toDigits(saved.phone || "").slice(0, 10));
  const [wilaya, setWilaya] = useState(saved.willaya || ""); // French name, as production sends it
  const [photo, setPhoto] = useState(null); // { file, url }
  const [consent, setConsent] = useState(false);
  const [bad, setBad] = useState({}); // field -> error key
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false); // the camera with the receipt frame
  const cameraRef = useRef(null); // the phone's camera app, when the page cannot open the camera
  const galleryRef = useRef(null);

  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  useEffect(() => () => photo && URL.revokeObjectURL(photo.url), [photo]);

  const shows = (field) => fields.includes(field);
  const needs = (field) => obligatory.includes(field);
  // the photo step only when the tenant asks for the receipt
  const steps = ["info", ...(shows("img_l") ? ["photo"] : []), "confirm"];
  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const titles = { info: t("tbS1"), photo: t("tbS2"), confirm: t("tbS3") };
  const goStep = (name) => setStepIndex(steps.indexOf(name));

  if (!enabledModules.lottery) return <Navigate to="/" replace />;

  const wilayaOf = (text) => wilayas.find((w) => w.text === text);
  const wilayaLabel = (text) => {
    const w = wilayaOf(text);
    return w ? `${String(w.id).padStart(2, "0")} · ${lang === "ar" ? w.text_ar : w.text}` : text;
  };

  const checkInfo = () => {
    const next = {};
    if ((needs("name") || name) && shows("name") && name.trim().length < 3) next.name = "tbErrName";
    if ((needs("phone") || phone) && shows("phone") && !phoneOk(phone)) next.phone = "tbErrTel";
    if (needs("willaya") && shows("willaya") && !wilaya) next.willaya = "tbErrWil";
    setBad(next);
    const first = Object.keys(next)[0];
    if (first) flagField(first);
    return !first;
  };

  const pickPhoto = async (e) => {
    const { items, error: readError } = await readImages(e.target, 1);
    if (readError) setError(t(readError));
    if (items[0]) {
      setError("");
      setPhoto(items[0]);
    }
  };

  const submit = async () => {
    if (sending) return;
    if (!consent) {
      setError(t("tbErrConsent"));
      return flagField("consent");
    }
    setError("");
    setSending(true);
    const formData = new FormData();
    name && formData.append("name", name.trim());
    phone && formData.append("phone", phone);
    wilaya && formData.append("willaya", wilaya);
    photo && formData.append("img_l", new File([photo.file], photo.file.name || "ticket.jpg", { type: photo.file.type }));
    try {
      await axios.post(`${backendLinkLottery}addLotteryForm`, formData, {
        withCredentials: true, // Include cookies for CSRF protection
        headers: { "Content-Type": "multipart/form-data" },
      });
      try {
        localStorage.setItem(SAVED_KEY, JSON.stringify({ name, phone, email: "", willaya: wilaya }));
      } catch (_) {}
      setSent(true);
    } catch (err) {
      console.error("Error sending participation:", err.response?.data || err);
      setError(err.response?.status === 422 ? err.response?.data?.message || t("suggestionErrorInvalid") : t("suggestionErrorServer"));
    } finally {
      setSending(false);
    }
  };

  const infoSub = [name.trim(), phone && spacedTel(phone), wilaya && (wilayaOf(wilaya) ? (lang === "ar" ? wilayaOf(wilaya).text_ar : wilaya) : wilaya)]
    .filter(Boolean)
    .join(" · ");

  // the number stays left to right inside the Arabic sentence, and on one line
  const telIsolated = `\u2066${spacedTel(phone).replace(/ /g, "\u00a0")}\u2069`;
  const done = sent && <ThanksScreen title={t("tbOkTitle")} text={t("tbOkText", { tel: telIsolated })} onHome={() => navigate("/")} />;

  return (
    <PageShell
      title={t("tbTitle")}
      // back: the previous step, then the home page
      onBack={() => (stepIndex > 0 && !sent ? setStepIndex(stepIndex - 1) : navigate("/"))}
      currentLang={currentLang}
      setCurrentLang={setCurrentLang}
      done={done}
    >
      <div className={styles.cardBody}>
        <div className={styles.tbPrize}>
          <span className={styles.tbGift}>
            <Gift size={22} strokeWidth={1.8} aria-hidden />
          </span>
          <b>{t("tbPrize")}</b>
        </div>

        <div className={styles.tbProg}>
          <div className={styles.tbProgRow}>
            <span className={styles.tbProgN}>{t("tbStep", { n: stepIndex + 1, total: steps.length })}</span>
            <span className={styles.tbProgT}>{titles[step]}</span>
          </div>
          <div className={styles.tbBars} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
            {steps.map((s, i) => (
              <i key={s} className={classNames({ [styles.isOn]: i <= stepIndex })} />
            ))}
          </div>
        </div>

        <div className={styles.tbSteps}>
          {steps.map((s, i) => {
            const n = i + 1;
            if (i < stepIndex) {
              return s === "info" ? (
                <DoneRow key={s} title={t("tbS1")} sub={infoSub} onEdit={() => goStep("info")} />
              ) : (
                <DoneRow key={s} title={t("tbPhotoOk")} thumb={photo?.url} onEdit={() => goStep("photo")} />
              );
            }
            if (i > stepIndex) return <TodoRow key={s} n={n} title={titles[s]} />;

            if (s === "info")
              return (
                <StepCard key={s} n={n} title={t("tbS1")}>
                  {shows("name") && (
                    <Field label={t("tbName")} field="name" hint={bad.name && t(bad.name)} hintBad>
                      <input
                        type="text"
                        className={classNames(styles.input, { [styles.isBad]: bad.name })}
                        autoComplete="name"
                        placeholder={t("tbNamePh")}
                        value={name}
                        onChange={(e) => {
                          setName(cleanText(e.target.value).replace(/[0-9٠-٩,.!?()،؟]/g, ""));
                          setBad((b) => ({ ...b, name: "" }));
                        }}
                      />
                    </Field>
                  )}
                  {shows("phone") && (
                    <Field label={t("tbTel")} field="phone" hint={t(bad.phone || "tbTelHint")} hintBad={!!bad.phone}>
                      <input
                        type="tel"
                        className={classNames(styles.input, styles.ltr, { [styles.isBad]: bad.phone })}
                        autoComplete="tel"
                        inputMode="numeric"
                        placeholder={t("tbTelPh")}
                        value={phone}
                        onChange={(e) => {
                          setPhone(toDigits(e.target.value).slice(0, 10));
                          setBad((b) => ({ ...b, phone: "" }));
                        }}
                      />
                    </Field>
                  )}
                  {shows("willaya") && (
                    <Field label={t("tbWil")} field="willaya" hint={bad.willaya && t(bad.willaya)} hintBad>
                      <Select
                        value={wilaya}
                        placeholder={t("tbWilPh")}
                        className={classNames(styles.select, { [styles.isBad]: bad.willaya })}
                        options={wilayas.map((w) => ({ value: w.text, label: wilayaLabel(w.text) }))}
                        onChange={(v) => {
                          setWilaya(v);
                          setBad((b) => ({ ...b, willaya: "" }));
                        }}
                      />
                    </Field>
                  )}
                  <button type="button" className={classNames(styles.next, styles.block)} onClick={() => checkInfo() && setStepIndex(i + 1)}>
                    {t("tbNext")}
                  </button>
                </StepCard>
              );

            if (s === "photo")
              return (
                <StepCard key={s} n={n} title={t("tbS2")}>
                  <div className={classNames(styles.tbFrame, { [styles.tbHas]: photo })}>
                    {["c1", "c2", "c3", "c4"].map((c) => (
                      <span key={c} className={classNames(styles.tbCorner, styles[c])} />
                    ))}
                    {photo ? (
                      <>
                        <img className={styles.tbShot} src={photo.url} alt={t("tbPhotoOk")} />
                        <button type="button" className={styles.tbRetake} onClick={() => setCameraOpen(true)}>
                          {t("tbRetake")}
                        </button>
                      </>
                    ) : (
                      // a receipt drawn in the frame
                      <div className={styles.tbPaper} aria-hidden="true">
                        <i className={styles.h} />
                        <i />
                        <i />
                        <i className={styles.s} />
                        <i />
                        <i className={styles.t} />
                      </div>
                    )}
                  </div>
                  <p className={styles.tbFrameHint}>{t("tbFrame")}</p>
                  <ErrorBox>{error}</ErrorBox>
                  {photo ? (
                    <button type="button" className={classNames(styles.next, styles.block)} onClick={() => setStepIndex(i + 1)}>
                      {t("tbNext")}
                    </button>
                  ) : (
                    <div className={styles.tbTwo}>
                      <button type="button" className={classNames(styles.next, styles.block)} onClick={() => setCameraOpen(true)}>
                        <Camera size={18} strokeWidth={2} aria-hidden />
                        <span>{t("tbCam")}</span>
                      </button>
                      <button type="button" className={classNames(styles.ghost, styles.block)} onClick={() => galleryRef.current?.click()}>
                        <ImageIcon size={18} strokeWidth={2} aria-hidden />
                        <span>{t("tbGal")}</span>
                      </button>
                    </div>
                  )}
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={pickPhoto} />
                  <input ref={galleryRef} type="file" accept="image/*" hidden onChange={pickPhoto} />
                </StepCard>
              );

            return (
              <StepCard key={s} n={n} title={t("tbS3")}>
                <div className={styles.tbRecap}>
                  {shows("name") && (
                    <div>
                      <span>{t("tbRecapName")}</span>
                      <b>{name.trim()}</b>
                    </div>
                  )}
                  {shows("phone") && (
                    <div>
                      <span>{t("tbRecapTel")}</span>
                      <b dir="ltr">{spacedTel(phone)}</b>
                    </div>
                  )}
                  {shows("willaya") && (
                    <div>
                      <span>{t("tbRecapWil")}</span>
                      <b>{wilayaLabel(wilaya)}</b>
                    </div>
                  )}
                </div>
                <label className={styles.tbConsent} data-field="consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => {
                      setConsent(e.target.checked);
                      setError("");
                    }}
                  />
                  <span>
                    <Trans
                      i18nKey="tbConsent"
                      components={{
                        rules: (
                          <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={(e) => {
                              e.preventDefault();
                              setRulesOpen(true);
                            }}
                          />
                        ),
                      }}
                    />
                  </span>
                </label>
                <ErrorBox>{error}</ErrorBox>
                <button type="button" className={styles.tbGo} disabled={sending} onClick={submit}>
                  {sending ? t("suggestionSending") : t("tbGo")}
                </button>
                <p className={styles.tbNote}>{t("tbNote")}</p>
              </StepCard>
            );
          })}
        </div>
      </div>

      {cameraOpen && (
        <Suspense fallback={null}>
          <ReceiptCamera
            onShot={(shot) => {
              setError("");
              setPhoto(shot);
              setCameraOpen(false);
            }}
            onClose={() => setCameraOpen(false)}
            onFallback={() => {
              setCameraOpen(false);
              cameraRef.current?.click(); // still inside the tap: the browser lets it open
            }}
          />
        </Suspense>
      )}

      {/* the rules of the client's lottery (production modal of the contest) */}
      <Suspense fallback={null}>
        <ParticipationTermsModal
          isOpen={rulesOpen}
          onAccept={() => {
            setConsent(true);
            setError("");
            setRulesOpen(false);
          }}
          onRefuse={() => setRulesOpen(false)}
        />
      </Suspense>
    </PageShell>
  );
}
